/**
 * AIMDv2 – API Layer
 *
 * Thin wrappers around the global `API` object (src/api/index.ts) that
 * adapt backend responses to the types this module expects.
 *
 * Auth headers (Authorization, idtoken, location) are injected automatically
 * by the global axios request interceptor in App.tsx. We only need to pass
 * context-specific headers like `groupid` where required.
 *
 * Audience options come from the Member Attributes endpoints.
 * Tone options have no backend endpoint — static frontend data.
 */

import axios from 'axios';
import {
  API,
  CourseDraftSaveReason,
  ProgramGenerationContentLessonPatch,
  ProgramGenerationContentMcq,
  ProgramGenerationContentModulePatch,
  ProgramGenerationContentSaveResponse
} from '../../../../../api';
import {
  AIMDv2WizardState,
  AIMDv2BuildState,
  DocumentItem,
  DocumentProcessingStatus,
  AudienceOption,
  MemberAttributeGroup,
  AIMD_STEPS,
  ProgramStructure,
  GeneratedLesson,
  GeneratedModule,
  GenerationStatus,
  MCQ,
  ToneOption
} from '../types';
import {
  buildGroupPutPayload,
  normalizeOrgCatalog,
  parseGroupMemberAttributesResponse,
  unwrapEnvelope,
  MemberAttributeGroupCatalog
} from '../../../utils/memberAttributes';
import { INITIAL_WIZARD_STATE } from '../constants';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Convert the frontend wizard state into the buildState shape the backend expects. */
export function toBuildState(state: AIMDv2WizardState): AIMDv2BuildState {
  const allDocs = [...state.documents.uploadedDocuments, ...state.documents.previousDocuments];
  const selectedDocIds = allDocs.filter((d) => d.selected).map((d) => d.id);

  return {
    currentStep: state.currentStep,
    highestCompletedStep: state.highestCompletedStep,
    requirements: state.requirements,
    outcomes: {
      learningObjectives: state.outcomes.outcomes
        .map((o) => o.value)
        .filter((v) => v.trim().length > 0)
    },
    documents: { selectedDocIds },
    preview: state.preview.generatedPreview,
    ai: {
      structure: state.structure.generatedStructure,
      content: state.content.generatedContent
    },
    generationId: state.generationId
  };
}

/**
 * Map a backend OrgDocument into our frontend DocumentItem.
 * `type` in backend can be "file" | "text" | "webpage"; we normalise "webpage" → "url".
 */
function mapOrgDocToItem(
  doc: any,
  opts: { isNew?: boolean; selected?: boolean } = {}
): DocumentItem {
  const backendType = doc.type || doc.documentType || 'file';
  const type = backendType === 'webpage' ? 'url' : backendType;

  let fileTypeLabel = 'FILE';
  if (type === 'text' && doc.source === 'program') {
    // Docs auto-created from a published program are course content, not a
    // plain text upload — surface that with a distinct COURSE badge.
    fileTypeLabel = 'COURSE';
  } else if (type === 'text') {
    fileTypeLabel = 'TEXT';
  } else if (type === 'url') {
    fileTypeLabel = 'URL';
  } else if (doc.fileType) {
    const ext = doc.fileType.split('/').pop()?.toUpperCase();
    fileTypeLabel = ext || 'FILE';
  } else if (doc.fileName) {
    fileTypeLabel = doc.fileName.split('.').pop()?.toUpperCase() || 'FILE';
  }

  return {
    id: doc._id || doc.id,
    type: type as DocumentItem['type'],
    title: doc.title || doc.documentTitle || doc.fileName || 'Untitled',
    fileTypeLabel,
    instructions: doc.instruction || '',
    processingStatus: (doc.documentProcessingStatus as DocumentProcessingStatus) || null,
    documentUrl: doc.documentUrl || undefined,
    textContent: doc.textContent || undefined,
    url: type === 'url' ? (doc.documentUrl || doc.websiteUrl) : undefined,
    isNew: opts.isNew ?? false,
    selected: opts.selected ?? false
  };
}

// ---------------------------------------------------------------------------
// Member Attributes (Program Audience)
// ---------------------------------------------------------------------------

/** Flatten a normalized org catalog into the flat AudienceOption list the dropdown renders. */
function catalogToAudienceOptions(orgCatalog: MemberAttributeGroupCatalog[]): AudienceOption[] {
  const options: AudienceOption[] = [];
  for (const group of orgCatalog) {
    for (const val of group.values) {
      options.push({
        value: val._id,
        label: val.value,
        category: group.name,
        categoryId: group._id
      });
    }
  }
  return options;
}

/**
 * Build dropdown options directly from the org-level member-attribute catalog
 * (e.g. `userProfile.data.orgMemberAttributes` in Redux).
 *
 * Used during a *new* program creation, where no groupId exists yet so the
 * Member Attributes endpoint can't be hit — the org catalog is org-level data
 * that is the same regardless of group.
 */
export function buildAudienceOptionsFromOrgGroups(
  orgGroups: MemberAttributeGroup[] | undefined
): { options: AudienceOption[]; orgAttributeGroups: MemberAttributeGroup[] } {
  const orgCatalog = normalizeOrgCatalog(orgGroups);
  return {
    options: catalogToAudienceOptions(orgCatalog),
    orgAttributeGroups: orgCatalog as MemberAttributeGroup[]
  };
}

/**
 * Fetch the org's attribute catalog + this program's current selection.
 * Returns:
 *  - options: flat list of AudienceOption for the dropdown
 *  - selectedIds: value _ids already linked to this program
 *  - orgAttributeGroups: raw backend groups (needed for the PUT payload)
 */
export async function fetchAudienceOptions(
  groupId: string,
  profileOrgFallback?: MemberAttributeGroup[]
): Promise<{
  options: AudienceOption[];
  selectedIds: string[];
  orgAttributeGroups: MemberAttributeGroup[];
}> {
  const res = await API.getGroupMemberAttributes({ grpId: groupId });
  let data = unwrapEnvelope(res) as {
    orgMemberAttributes?: MemberAttributeGroup[];
    grpMemberAttributes?: string[];
  };
  if (
    Array.isArray(data?.orgMemberAttributes) &&
    data.orgMemberAttributes.length === 0 &&
    Array.isArray(profileOrgFallback) &&
    profileOrgFallback.length > 0
  ) {
    data = { ...data, orgMemberAttributes: profileOrgFallback };
  }
  const { orgCatalog, grpIds } = parseGroupMemberAttributesResponse({ data } as unknown, undefined);

  return {
    options: catalogToAudienceOptions(orgCatalog),
    selectedIds: grpIds,
    orgAttributeGroups: orgCatalog as MemberAttributeGroup[]
  };
}

/**
 * Save the audience selection for this program.
 * Builds the full PUT payload with selected: true/false for every value.
 *
 * `options.enroll: false` (creation-flow revamp) saves WITHOUT the BE's
 * enroll-matching-members side effect — enrollment then only happens on
 * "Invite & Finish". Omitted keeps the legacy enroll-on-save behaviour.
 */
export async function saveAudienceSelection(
  groupId: string,
  selectedIds: string[],
  orgAttributeGroups: MemberAttributeGroup[],
  options?: { enroll?: boolean }
): Promise<void> {
  const selectedSet = new Set(selectedIds);

  const payload = buildGroupPutPayload(
    orgAttributeGroups.map((group) => ({
      _id: group._id,
      name: group.name,
      values: group.values.map((val) => ({
        _id: val._id,
        value: val.value,
        selected: selectedSet.has(val._id)
      }))
    }))
  );

  await API.updateGroupMemberAttributes({ grpId: groupId, data: payload, enroll: options?.enroll });
}

// ---------------------------------------------------------------------------
// Program Generation (Draft)
// ---------------------------------------------------------------------------

/**
 * Create a new program-generation record.
 * The backend auto-creates a fresh group (AIMD-{timestamp}) so no existing
 * program can be overwritten.
 * Returns both the generationId and the new groupId.
 */
export async function createProgramGeneration(
  existingGroupId?: string
): Promise<{ generationId: string; groupId: string }> {
  const res = await API.generateProgramId(existingGroupId);
  const data = res.data?.data || res.data;
  return {
    generationId: data.id,
    groupId: data.groupId
  };
}

/**
 * Rename the auto-created group to the user's chosen program name.
 * Called after Step 1 (Requirements) when the user clicks Next.
 */
export async function renameProgramGroup(
  generationId: string,
  programName: string
): Promise<void> {
  await axios.patch(
    `${process.env.REACT_APP_API}/program-generation/${generationId}/group-name`,
    { programName }
  );
}

// ---------------------------------------------------------------------------
// Build State (Auto-save)
// ---------------------------------------------------------------------------

/** Save the full wizard state to the Group's buildState field. */
export async function saveBuildState(
  groupId: string,
  state: AIMDv2WizardState
): Promise<void> {
  const buildState = toBuildState(state);
  await API.saveBuildState({ grpId: groupId, data: buildState });
}

/** Read the persisted buildState for a group (null when nothing saved yet). */
export async function loadBuildState(groupId: string): Promise<AIMDv2BuildState | null> {
  const res: any = await API.getBuildState({ grpId: groupId });
  const data = res?.data ?? res ?? null;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return null;
  return data as AIMDv2BuildState;
}

/**
 * Rebuild a full wizard state from a persisted buildState so the admin can
 * resume an in-progress AI program at the exact step they left — driven by
 * the BE record (not localStorage) so it works cross-device and always
 * targets the program that was opened. Lossy fields the buildState doesn't
 * carry (document objects, preview, members) start empty and are re-fetched
 * by their own step on demand.
 */
export function fromBuildState(
  bs: AIMDv2BuildState | null,
  groupId: string
): AIMDv2WizardState {
  const base: AIMDv2WizardState = { ...INITIAL_WIZARD_STATE };
  if (!bs) return { ...base, groupId };
  const objectives = bs.outcomes?.learningObjectives ?? [];

  // Resume at the FURTHEST point the data proves we reached — generated
  // content/structure, generated objectives, or the recorded completion
  // history. We deliberately do NOT clamp by `bs.currentStep`:
  //   • goNext persists the PRE-advance snapshot, so currentStep lags the real
  //     position (e.g. structure/content generated while sitting on a step) —
  //     clamping by it sent cross-device resumes back to Documents; and
  //   • the legacy /new-program AI sub-flow writes a DIFFERENT, incompatible
  //     step scale into this same field, which used to make brand-new programs
  //     jump to "stage 2".
  // Data presence + highestCompletedStep are the reliable signals; the
  // generation-record recovery in hydrateWizardFromBackend fills any artifact
  // the buildState didn't snapshot and bumps the step accordingly.
  const stepFromData = bs.ai?.content
    ? AIMD_STEPS.indexOf('content')
    : bs.ai?.structure
    ? AIMD_STEPS.indexOf('structure')
    : bs.preview
    ? AIMD_STEPS.indexOf('preview')
    : objectives.length
    ? AIMD_STEPS.indexOf('outcomes')
    : 0;
  const highestCompleted =
    typeof bs.highestCompletedStep === 'number' ? bs.highestCompletedStep : -1;
  const lastStep = AIMD_STEPS.length - 1;
  const currentStep = Math.min(lastStep, Math.max(0, stepFromData, highestCompleted + 1));

  return {
    ...base,
    currentStep,
    highestCompletedStep: Math.max(highestCompleted, currentStep - 1),
    requirements: { ...base.requirements, ...(bs.requirements || {}) },
    outcomes: {
      outcomes: objectives.length
        ? objectives.map((v) => ({ id: crypto.randomUUID(), value: v }))
        : base.outcomes.outcomes
    },
    preview: { generatedPreview: bs.preview ?? null },
    structure: { generatedStructure: (bs.ai?.structure ?? null) as ProgramStructure | null },
    content: { generatedContent: (bs.ai?.content ?? null) as GenerationStatus | null },
    generationId: bs.generationId ?? null,
    groupId
  };
}

/**
 * Full resume hydration: rebuild the wizard state from the BE buildState and,
 * when the admin paused on (or past) the content step without the generated
 * lessons being snapshotted into buildState, pull the live generation record
 * so the step reopens with real content instead of silently regenerating.
 */
export async function hydrateWizardFromBackend(groupId: string): Promise<AIMDv2WizardState> {
  const bs = await loadBuildState(groupId);
  let state = fromBuildState(bs, groupId);

  // The buildState can lag the real generation: goNext persists the pre-advance
  // snapshot and never captures the structure/content produced while sitting on
  // a step. When a generation record exists, treat IT as the source of truth
  // for how far the build actually got — recover the structure/content and
  // advance the resume step, so a cross-device resume reopens on the right step
  // WITH the generated artifacts (instead of snapping back to Documents).
  if (state.generationId) {
    try {
      const server = await pollGenerationStatus(state.generationId);
      const hasStructure = !!server && Array.isArray(server.modules) && server.modules.length > 0;
      if (hasStructure) {
        const structureStepIndex = AIMD_STEPS.indexOf('structure');
        const contentStepIndex = AIMD_STEPS.indexOf('content');
        const lastStep = AIMD_STEPS.length - 1;

        // Content is REALLY present only when a lesson actually carries generated
        // content. We must NOT infer it from `status`/`progress.totalLessons` —
        // those are populated by the structure alone (the lesson SLOTS exist
        // before any content is generated). Landing on the Content step with no
        // real content makes that step auto-fire generation (ContentStep mounts
        // and calls handleGenerateContent when generatedContent is null),
        // hijacking an admin who only generated the structure and still wants to
        // edit it before generating content.
        const hasContent = server.modules.some(
          (m: any) => Array.isArray(m.lessons) && m.lessons.some((l: any) => l?.content)
        );

        // Recover the artifacts so the step we land on doesn't auto-regenerate
        // (both Structure and Content steps re-fire generation when their
        // artifact is null). The record's modules are a superset of
        // ProgramStructure's, so reuse them directly.
        if (!state.structure.generatedStructure) {
          state = {
            ...state,
            structure: {
              generatedStructure: { modules: server.modules } as unknown as ProgramStructure
            }
          };
        }
        if (hasContent && !state.content.generatedContent) {
          state = { ...state, content: { generatedContent: server as GenerationStatus } };
        }

        // Floor the resume step at the furthest artifact that ACTUALLY exists:
        //   • content present  → Content step (we recovered generatedContent, so
        //     it resumes polling/display instead of regenerating);
        //   • structure only   → Structure step, so the admin can review/edit it
        //     and choose when to generate content — never auto-advance into the
        //     content generation.
        const floorStep = hasContent ? contentStepIndex : structureStepIndex;
        if (floorStep >= 0 && state.currentStep < floorStep) {
          state = {
            ...state,
            currentStep: Math.min(lastStep, floorStep),
            highestCompletedStep: Math.max(state.highestCompletedStep, floorStep - 1)
          };
        }
      }
    } catch (err) {
      console.error('[AIMDv2] generation fetch on resume failed', err);
    }
  }

  return state;
}

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

/** Fetch all org documents for the given group. Returns raw array. */
export async function fetchOrgDocuments(groupId: string): Promise<DocumentItem[]> {
  const res: any = await API.getOrganizationDocuments(groupId);
  // getOrganizationDocuments already does .then(res => res.data), so `res` is the
  // GetOrgDocsResponse directly — but guard against both shapes just in case.
  const docs = res?.documents || res?.data?.documents || [];
  return docs.map((doc: any) => mapOrgDocToItem(doc));
}

/** Upload a file document. Returns the new document item. */
export async function uploadFileDocument(
  file: File,
  groupId: string,
  instruction?: string
): Promise<DocumentItem> {
  const res = await API.processDocument(file, groupId, instruction);
  const data = res.data?.data || res.data;
  const ext = file.name.split('.').pop()?.toUpperCase() || 'FILE';

  return {
    id: data.id,
    type: 'file',
    title: file.name.replace(/\.[^/.]+$/, ''),
    fileTypeLabel: ext,
    instructions: instruction || '',
    processingStatus: 'processing',
    file,
    isNew: true,
    selected: true
  };
}

/** Create a text-based document. Returns the new document item. */
export async function uploadTextDocument(
  title: string,
  content: string,
  groupId: string,
  instruction?: string
): Promise<DocumentItem> {
  const res = await API.processTextDocument({ title, content, groupId, instruction });
  const data = res.data?.data || res.data;

  return {
    id: data.id,
    type: 'text',
    title,
    fileTypeLabel: 'TEXT',
    instructions: instruction || '',
    processingStatus: 'processing',
    textContent: content,
    isNew: true,
    selected: true
  };
}

/** Create a URL-based document. Returns the new document item. */
export async function uploadUrlDocument(
  title: string,
  websiteUrl: string,
  groupId: string,
  instruction?: string
): Promise<DocumentItem> {
  const res = await API.processWebsiteDocument({ title, websiteUrl, groupId, instruction });
  const data = res.data?.data || res.data;

  return {
    id: data.id,
    type: 'url',
    title,
    fileTypeLabel: 'URL',
    instructions: instruction || '',
    processingStatus: 'processing',
    url: websiteUrl,
    isNew: true,
    selected: true
  };
}

/** Update the instruction of an existing document. */
export async function updateInstruction(
  orgDocsId: string,
  instruction: string
): Promise<void> {
  await API.updateDocumentInstruction(orgDocsId, instruction);
}

/** Poll the processing status of a document. */
export async function getProcessingStatus(
  orgDocsId: string
): Promise<DocumentProcessingStatus> {
  const res = await API.getDocumentProcessingStatus(orgDocsId);
  return res.data?.data?.status || res.data?.status || 'processing';
}

// ---------------------------------------------------------------------------
// Preview (Step 4)
// ---------------------------------------------------------------------------

export async function generatePreview(
  generationId: string,
  payload: { generalPurpose: string; learningObjectives: string[]; programName?: string }
): Promise<{ generationId: string; preview: string }> {
  const res = await API.generatePreview({ programGenerationId: generationId, ...payload });
  const data = res.data?.data || res.data;
  return { generationId: data.generationId || generationId, preview: data.preview || '' };
}

export async function improvePreview(
  generationId: string,
  payload: {
    generalPurpose: string;
    learningObjectives: string[];
    currentPreview: string;
    feedback: string;
    programName?: string;
  }
): Promise<{ generationId: string; preview: string }> {
  const res = await API.improvePreview({ programGenerationId: generationId, ...payload });
  const data = res.data?.data || res.data;
  return { generationId: data.generationId || generationId, preview: data.preview || '' };
}

// Canonical tone list (GET /program-generation/tones). Callers keep the static
// list as a fallback so the tone step never renders empty.
export async function fetchToneOptions(): Promise<ToneOption[]> {
  const res = await API.getProgramTones();
  const data = res.data?.data || res.data;
  const tones = Array.isArray(data?.tones) ? data.tones : [];
  return tones.filter(
    (t: any): t is ToneOption =>
      t && typeof t.value === 'string' && typeof t.label === 'string'
  );
}

// ---------------------------------------------------------------------------
// Structure (Step 5)
// ---------------------------------------------------------------------------

export async function generateStructure(
  generationId: string,
  payload: {
    generalPurpose: string;
    learningObjectives: string[];
    groupId: string;
    orgDocsIds?: string[];
    programPreview?: string;
  }
): Promise<{ modules: any[] }> {
  const res = await API.generateProgramStructure({
    programGenerationId: generationId,
    ...payload
  });
  const data = res.data?.data || res.data;
  return data?.programStructure || data || { modules: [] };
}

export async function saveManualStructure(
  generationId: string,
  modules: Array<{
    title: string;
    reasoning?: string;
    learningOutcomesServed?: string[];
    ragDocumentTitles?: string[];
    lessons: Array<{ title: string; reasoning?: string; capabilityId?: string | null; outcomes?: string[] }>;
  }>
): Promise<{ modules: any[] }> {
  const res = await API.saveStructure({ programGenerationId: generationId, modules });
  const data = res.data?.data || res.data;
  return data || { modules };
}

export async function improveModule(
  generationId: string,
  payload: {
    generalPurpose: string;
    learningObjectives: string[];
    moduleIndex: number;
    feedback: string;
    programPreview?: string;
  }
): Promise<{ moduleIndex: number; module: any; modules?: any[] }> {
  const res = await API.improveModuleStructure({
    programGenerationId: generationId,
    ...payload
  });
  const data = res.data?.data || res.data;
  // The Capability build flow answers with the whole structure too: its Roleplays may have moved.
  return {
    moduleIndex: data.moduleIndex ?? payload.moduleIndex,
    module: data.module,
    ...(Array.isArray(data.modules) ? { modules: data.modules } : {})
  };
}

export async function improveProgram(
  generationId: string,
  payload: {
    generalPurpose: string;
    learningObjectives: string[];
    feedback: string;
    programPreview?: string;
  }
): Promise<{ modules: any[] }> {
  const res = await API.improveProgramStructure({
    programGenerationId: generationId,
    ...payload
  });
  const data = res.data?.data || res.data;
  return data?.programStructure || data || { modules: [] };
}

// ---------------------------------------------------------------------------
// Content (Step 6)
// ---------------------------------------------------------------------------

export async function generateContent(
  generationId: string,
  payload: {
    generalPurpose: string;
    learningObjectives: string[];
    modules: Array<{ title: string; lessons: Array<{ title: string; capabilityId?: string; outcomes?: string[] }> }>;
    orgDocsIds?: string[];
    programPreview?: string;
  }
): Promise<{ generationId: string; status: string; progress: any }> {
  const res = await API.generateProgramContent({
    programGenerationId: generationId,
    ...payload,
    orgDocsIds: payload.orgDocsIds || []
  });
  const data = res.data?.data || res.data;
  return data;
}

export async function pollGenerationStatus(generationId: string): Promise<any> {
  const res = await API.getProgramContent({ generationId });
  const data = res.data?.data || res.data;
  return data;
}

export async function regenerateProgramContent(
  generationId: string,
  feedback: string,
  modules?: { title: string; lessons: { title: string; capabilityId?: string; outcomes?: string[] }[] }[]
): Promise<any> {
  const res = await API.regenerateProgram({ programGenerationId: generationId, feedback, modules });
  return res.data?.data || res.data;
}

export async function regenerateModuleContent(
  generationId: string,
  moduleIndex: number,
  feedback: string
): Promise<any> {
  const res = await API.regenerateModule({
    programGenerationId: generationId,
    moduleIndex,
    feedback
  });
  return res.data?.data || res.data;
}

export async function regenerateLessonContent(
  generationId: string,
  moduleIndex: number,
  lessonIndex: number,
  feedback: string
): Promise<any> {
  const res = await API.regenerateLesson({
    programGenerationId: generationId,
    moduleIndex,
    lessonIndex,
    feedback
  });
  return res.data?.data || res.data;
}

// ---------------------------------------------------------------------------
// Content save (PATCH program-generation/{id}/content)
// ---------------------------------------------------------------------------
//
// The Content step used to persist only titles (PATCH .../structure); the
// lesson body, quiz and materials lived in the browser until Publish. This
// writes the edited lessons onto the generation artifact so they survive a
// reload and are what Publish materialises onto the template
// (docs/API-course-versioning.md §2b "Content save"). Only the lessons the
// admin changed are sent, each in full, so the artifact copy of a touched
// lesson always equals the browser's.

/** `${moduleIndex}:${lessonIndex}` — the artifact addresses lessons by position (no ids on the status payload). */
export const contentLessonKey = (moduleIndex: number, lessonIndex: number): string =>
  `${moduleIndex}:${lessonIndex}`;

export function parseContentLessonKey(key: string): { moduleIndex: number; lessonIndex: number } | null {
  const [m, l] = key.split(':').map(Number);
  if (!Number.isInteger(m) || !Number.isInteger(l) || m < 0 || l < 0) return null;
  return { moduleIndex: m, lessonIndex: l };
}

const CONTENT_LESSON_FIELDS = ['title', 'content', 'mcqs', 'mcq', 'materials'] as const;

const sameValue = (a: unknown, b: unknown): boolean =>
  a === b || JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

const lessonChanged = (prev: GeneratedLesson | undefined, next: GeneratedLesson): boolean =>
  prev !== next && (!prev || CONTENT_LESSON_FIELDS.some((f) => !sameValue(prev[f], next[f])));

/**
 * Keys of the lessons that differ between two generation states, by
 * position. A module whose title changed marks every lesson in it: the
 * structure save (PATCH .../structure) matches modules and lessons by title
 * and scaffolds whatever it cannot match, so a renamed module's lessons lose
 * their artifact copy and must be written again. The same holds for a
 * renamed or moved lesson, which is caught by its own fields.
 */
export function collectChangedLessonKeys(
  prev: GenerationStatus | null | undefined,
  next: GenerationStatus | null | undefined
): string[] {
  if (!next?.modules || prev === next) return [];
  const keys: string[] = [];
  next.modules.forEach((m, mi) => {
    const prevModule = prev?.modules?.[mi];
    if (prevModule === m) return;
    const moduleRenamed = !prevModule || prevModule.title !== m.title;
    (m.lessons || []).forEach((l, li) => {
      if (moduleRenamed || lessonChanged(prevModule?.lessons?.[li], l)) keys.push(contentLessonKey(mi, li));
    });
  });
  return keys;
}

/** The editor's quiz question as the endpoint accepts it (answers as text, index or the encrypted value, untouched). */
const toContentMcq = (q: MCQ): ProgramGenerationContentMcq => ({
  question: q.question || '',
  options: (q.options || []).map((o) => String(o ?? '')),
  correct_answer: q.correct_answer || '',
  ...(typeof q.explanation === 'string' ? { explanation: q.explanation } : {}),
  ...(Array.isArray(q.optionReasonings) ? { optionReasonings: q.optionReasonings.map((r) => String(r ?? '')) } : {})
});

/**
 * One lesson as the endpoint stores it: everything the goal upload sends for
 * it (title, body, quiz, materials), so the artifact copy equals the browser
 * whatever the structure save scaffolded. The quiz is the editor's list
 * (`mcqs`) or the single `mcq`, the same choice buildGoalPayload makes; an
 * empty title is left out (the BE rejects it with 400).
 */
export function toContentLessonPatch(lesson: GeneratedLesson, lessonIndex: number): ProgramGenerationContentLessonPatch {
  const title = (lesson.title || '').trim();
  const quiz: MCQ[] = Array.isArray(lesson.mcqs) ? lesson.mcqs : lesson.mcq ? [lesson.mcq] : [];
  return {
    lessonIndex,
    ...(title ? { title } : {}),
    content: lesson.content || '',
    mcqs: quiz.filter(Boolean).map(toContentMcq),
    materials: (lesson.materials || []).map((m) => ({
      _id: String(m?._id || ''),
      title: m?.title || '',
      description: m?.description || ''
    }))
  };
}

/**
 * Group the dirty lessons into the endpoint's `modules` body. Keys that no
 * longer point at a lesson (index out of range) are skipped; the caller
 * clears them with the rest after a successful save.
 */
export function buildContentPatch(
  modules: GeneratedModule[] | null | undefined,
  keys: Iterable<string>
): ProgramGenerationContentModulePatch[] {
  const byModule = new Map<number, ProgramGenerationContentLessonPatch[]>();
  for (const key of keys) {
    const pos = parseContentLessonKey(key);
    if (!pos) continue;
    const lesson = modules?.[pos.moduleIndex]?.lessons?.[pos.lessonIndex];
    if (!lesson) continue;
    const list = byModule.get(pos.moduleIndex) || [];
    list.push(toContentLessonPatch(lesson, pos.lessonIndex));
    byModule.set(pos.moduleIndex, list);
  }
  return Array.from(byModule.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([moduleIndex, lessons]) => ({
      moduleIndex,
      lessons: lessons.sort((a, b) => a.lessonIndex - b.lessonIndex)
    }));
}

/** PATCH .../content → `{ updatedAt, lessonsUpdated, versionCreated }`. Throws on error (the caller keeps its dirty keys). */
export async function saveProgramGenerationContent(
  generationId: string,
  modules: ProgramGenerationContentModulePatch[],
  reason?: CourseDraftSaveReason
): Promise<ProgramGenerationContentSaveResponse> {
  const res = await API.saveProgramGenerationContent(generationId, {
    modules,
    ...(reason ? { reason } : {})
  });
  const data: any = res.data?.data || res.data || {};
  return {
    updatedAt: Number(data.updatedAt) || 0,
    lessonsUpdated: Number(data.lessonsUpdated) || 0,
    versionCreated: data.versionCreated === true
  };
}

/** Fire-and-forget keepalive flush for pagehide (reason 'close'); the response is never read. */
export function saveProgramGenerationContentKeepalive(
  generationId: string,
  modules: ProgramGenerationContentModulePatch[]
): void {
  API.saveProgramGenerationContentKeepalive(generationId, { modules });
}

// ---------------------------------------------------------------------------
// Goal Upload + Publish
// ---------------------------------------------------------------------------

interface GoalDate {
  type: 'calendarDate' | 'daysAfterSignup' | 'availableSoon' | 'activeNow';
  value: number;
}

interface GoalTask {
  taskName: string;
  taskDescription: string;
  taskPosition: number;
  startDate?: number;
  endDate?: number;
  menteeAsmtQues: string;
  menteeAsmtAnsType: string;
  menteeAsmtAnsOptions: string[];
  menteeQuestions: never[];
  MCQ: Array<{ ques: string; options: string[] }>;
  libraryArticles?: string[];
  /** AI-generation provenance (PRD §9.3), persisted on the template task. */
  reasoning?: string;
  learningOutcomesServed?: string[];
  ragDocumentTitles?: string[];
}

interface GoalLevel {
  level: number;
  levelName: string;
  levelDescription: string;
  startDate: GoalDate;
  endDate: GoalDate;
  CheckInWithMentor: string;
  tasks: GoalTask[];
}

interface GoalPayload {
  goalName: string;
  programType: string;
  levels: GoalLevel[];
}

/**
 * Map AI-generated modules/lessons into the goalUpload payload.
 * modules → levels, lessons → tasks.
 */
export function buildGoalPayload(
  programName: string,
  modules: Array<{
    title: string;
    reasoning?: string;
    learningOutcomesServed?: string[];
    lessons: Array<{
      title: string;
      content?: string;
      reasoning?: string;
      learningOutcomesServed?: string[];
      ragDocumentTitles?: string[];
      mcq?: { question: string; options: string[]; correct_answer: string } | null;
      mcqs?: Array<{ question: string; options: string[]; correct_answer: string }>;
      materials?: Array<{ _id?: string; title?: string }>;
    }>;
  }>
): GoalPayload {
  const levels: GoalLevel[] = modules.map((mod, mIdx) => {
    // Everything available immediately, same as the manual builder. The BE
    // date gate reads these as unix SECONDS — the previous weekly
    // `calendarDate` values here were Date.now() MILLISECONDS, which read as
    // far-future and locked every lesson's sidebar navigation for learners.
    const levelStart: GoalDate = { type: 'activeNow', value: 0 };
    const levelEnd: GoalDate = { type: 'activeNow', value: 0 };

    const tasks: GoalTask[] = mod.lessons.map((lesson, lIdx) => {
      const allMcqs = lesson.mcqs && lesson.mcqs.length > 0
        ? lesson.mcqs
        : lesson.mcq ? [lesson.mcq] : [];

      const libraryArticles = (lesson.materials || [])
        .map((m) => m._id)
        .filter((id): id is string => !!id && id.length > 0);

      return {
        taskName: lesson.title,
        taskDescription: lesson.content || '',
        taskPosition: lIdx + 1,
        startDate: 0,
        endDate: 0,
        menteeAsmtQues: 'Have you completed this lesson?',
        menteeAsmtAnsType: 'Yes, No',
        menteeAsmtAnsOptions: ['Yes', 'No'],
        menteeQuestions: [],
        MCQ: allMcqs.map((q) => {
          const opts = q.options || [];
          const numIdx = parseInt(q.correct_answer, 10);
          const correctAns =
            !isNaN(numIdx) && String(numIdx) === q.correct_answer && numIdx >= 0 && numIdx < opts.length
              ? opts[numIdx]
              : q.correct_answer || '';
          return {
            ques: q.question,
            options: opts,
            correctAns
          };
        }),
        ...(libraryArticles.length > 0 ? { libraryArticles } : {}),
        // AI-generation provenance (PRD §9.3) — persisted on the live template
        // task so the post-creation editor's AI Reasoning stays available.
        ...(lesson.reasoning ? { reasoning: lesson.reasoning } : {}),
        ...((lesson.learningOutcomesServed?.length || mod.learningOutcomesServed?.length)
          ? { learningOutcomesServed: lesson.learningOutcomesServed?.length ? lesson.learningOutcomesServed : mod.learningOutcomesServed }
          : {}),
        ...(lesson.ragDocumentTitles?.length ? { ragDocumentTitles: lesson.ragDocumentTitles } : {})
      };
    });

    return {
      level: mIdx + 1,
      levelName: mod.title,
      levelDescription: mod.reasoning || '',
      startDate: levelStart,
      endDate: levelEnd,
      CheckInWithMentor: 'no',
      tasks
    };
  });

  return {
    goalName: programName,
    programType: 'Masterclass',
    levels
  };
}

export async function goalUpload(
  grpId: string,
  payload: GoalPayload
): Promise<any> {
  const res = await axios({
    method: 'post',
    url: `${process.env.REACT_APP_API}/group/${grpId}/goal`,
    data: payload
  });
  return res.data?.data || res.data;
}

export async function publishProgramGeneration(
  grpId: string,
  programGenerationId: string
): Promise<void> {
  await axios({
    method: 'post',
    url: `${process.env.REACT_APP_API}/group/${grpId}/template/publish`,
    data: { program_generation_id: programGenerationId }
  });
}
